import { useMemo } from "react";
import { useLiveModel } from "../geo/services/useLiveModel";
import { buildTwin } from "../twin/twin";
import { correlateHazards } from "../geo/services/multiHazard";
import { assessServiceStress } from "../geo/services/serviceStress";
import { assessMobilityChain } from "../geo/services/mobilityChain";
import { simulateInterventions } from "../geo/services/interventions";
import { buildResponsePlan } from "../geo/services/emergencyResponse";
import { assessAssurance, time, LATENCY_BUDGET_MS } from "../assurance/assurance";
import { crowdSafetyPicture } from "../geo/services/crowdSafety";
import { FLOW_VECTORS } from "../geo/data/operational";

/* ──────────────────────────────────────────────────────────────────────────
 * One hook, every engine — the wiring layer between the §1–§22 work and the
 * pages.
 *
 * ── WHY A SINGLE HOOK ──
 * Before this, each page held its own hardcoded figures. The AI Assurance
 * page displayed "False Positive Rate 5.8%" while the §16 engine states in
 * terms that the false-positive rate cannot be computed at all — there is
 * no labelled ground truth. Two parts of the same product contradicting
 * each other is worse than either being wrong alone, because a reviewer who
 * catches it stops believing the rest.
 *
 * So every page now reads from here. There is one live model, one twin, one
 * hazard correlation. If a number appears on two pages it is the same
 * number, and if it changes it changes in both.
 *
 * ── ORDER MATTERS ──
 * The engines are composed in dependency order, and the correlator is given
 * the others' output. Four of the sixteen hazard types — traffic, utility,
 * route blockage and communications — can only be derived from the mobility
 * chain, the service engine and the assurance layer. Calling the correlator
 * without them makes those four report "unmonitored", which understates the
 * platform and overstates its blind spots.
 * ────────────────────────────────────────────────────────────────────────── */

export function useIntelligence({ running = true, speed = 10 } = {}) {
  const live = useLiveModel({ running, speed });

  const twin = useMemo(() => buildTwin(live.state), [live.state]);

  const services = useMemo(() => assessServiceStress(live.state), [live.state]);

  const mobility = useMemo(
    () => assessMobilityChain(live.state, { twin }),
    [live.state, twin],
  );

  /* Assurance is computed before hazards because the comms hazard reads
   * feed health from it. */
  const assuranceBase = useMemo(
    () => assessAssurance({ liveState: live.state, twin }),
    [live.state, twin],
  );

  const hazards = useMemo(
    () => correlateHazards(live.state, {
      twin, serviceStress: services, mobility, assurance: assuranceBase,
    }),
    [live.state, twin, services, mobility, assuranceBase],
  );

  const safety = useMemo(() => crowdSafetyPicture({ flows: FLOW_VECTORS }), []);

  /* Latency is genuinely measured — the only performance figure in the
   * platform that is not modelled. Timed here so every page showing an
   * assurance number shows the same measurement. */
  const timings = useMemo(() => [
    time("hazards", () => correlateHazards(live.state, { twin }), LATENCY_BUDGET_MS.hazards),
    time("services", () => assessServiceStress(live.state), LATENCY_BUDGET_MS.hazards),
    time("mobility", () => assessMobilityChain(live.state, { twin }), LATENCY_BUDGET_MS.mobility),
    time("twin", () => buildTwin(live.state), LATENCY_BUDGET_MS.twin),
  ], [live.state, twin]);

  const accuracy = live.accuracy();

  const assurance = useMemo(
    () => assessAssurance({
      liveState: live.state, twin, accuracy, timings,
      sessionStartedAt: SESSION_START,
    }),
    [live.state, twin, accuracy, timings],
  );

  /* The sector under most pressure — what almost every page wants as its
   * headline, computed once so no two pages disagree about which it is. */
  const worstSector = hazards.sectors[0] ?? null;

  return {
    live, twin, services, mobility, hazards, safety, assurance, accuracy, timings,
    worstSector,
    /* Lazily invoked: these are expensive and only some pages need them. */
    simulate: (sector) => simulateInterventions(live.state, sector, { twin }),
    respondTo: (incident) => buildResponsePlan(incident, live.state, { twin }),
  };
}

const SESSION_START = Date.now();
